import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';

const DailyReports = ({ apiClient }) => {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(false);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [availableDates, setAvailableDates] = useState([]);

  useEffect(() => {
    fetchAvailableReports();
    // Set default date range to last 30 days
    const today = new Date();
    const thirtyDaysAgo = new Date(today);
    thirtyDaysAgo.setDate(today.getDate() - 30);
    
    setEndDate(today.toISOString().split('T')[0]);
    setStartDate(thirtyDaysAgo.toISOString().split('T')[0]);
  }, []);

  const fetchAvailableReports = async () => {
    setLoading(true);
    try {
      const response = await apiClient.get('/restaurant/daily-reports');
      setReports(response.data);
      
      // Extract unique dates for the date picker
      const dates = response.data.map(report => report.report_date).sort();
      setAvailableDates(dates);
    } catch (error) {
      console.error('Error fetching daily reports:', error);
      toast.error('Failed to load daily reports');
    } finally {
      setLoading(false);
    }
  };

  const fetchReportsByDateRange = async () => {
    if (!startDate || !endDate) {
      toast.error('Please select both start and end dates');
      return;
    }

    if (new Date(startDate) > new Date(endDate)) {
      toast.error('Start date cannot be after end date');
      return;
    }

    setLoading(true);
    try {
      const response = await apiClient.get(`/restaurant/daily-reports`, {
        params: {
          start_date: startDate,
          end_date: endDate
        }
      });
      setReports(response.data);
    } catch (error) {
      console.error('Error fetching reports by date range:', error);
      toast.error('Failed to load reports for selected date range');
    } finally {
      setLoading(false);
    }
  };

  const downloadCombinedReport = async () => {
    if (!startDate || !endDate) {
      toast.error('Please select both start and end dates');
      return;
    }

    if (reports.length === 0) {
      toast.error('No reports available for selected date range');
      return;
    }

    setLoading(true);
    try {
      const response = await apiClient.get('/restaurant/daily-reports/download', {
        params: {
          start_date: startDate,
          end_date: endDate
        }
      });

      // Create and download the CSV file
      const csvContent = response.data.csv_content;
      const filename = response.data.filename;
      
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      toast.success(`Downloaded combined report: ${filename}`);
    } catch (error) {
      console.error('Error downloading combined report:', error);
      toast.error(error.response?.data?.detail || 'Failed to download combined report');
    } finally {
      setLoading(false);
    }
  };

  const downloadIndividualReport = async (reportDate) => {
    try {
      // For individual report, we'll use the same endpoint with single-day range
      const response = await apiClient.get('/restaurant/daily-reports/download', {
        params: {
          start_date: reportDate,
          end_date: reportDate
        }
      });

      const csvContent = response.data.csv_content;
      const filename = `daily_report_${reportDate}.csv`;
      
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      toast.success(`Downloaded report for ${reportDate}`);
    } catch (error) {
      console.error('Error downloading individual report:', error);
      toast.error('Failed to download individual report');
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <div className="space-y-6">
      {loading && (
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <div className="inline-block">
              <div className="spinner w-10 h-10 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
            </div>
            <p className="text-gray-600 mt-3">Loading daily reports...</p>
          </div>
        </div>
      )}

      {!loading && (
        <>
      {/* Header */}
      <div className="card">
        <div className="card-header">
          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center space-y-4 sm:space-y-0">
            <div>
              <h2 className="text-xl font-semibold text-gray-900">Daily Reports</h2>
              <p className="text-sm text-gray-600 mt-1">
                View and download daily order reports. Reports are automatically generated when orders are older than 24 hours.
              </p>
            </div>
            <button
              onClick={fetchAvailableReports}
              data-testid="refresh-reports-button"
              className="btn-secondary"
            >
              Refresh
            </button>
          </div>
        </div>
      </div>

      {/* Date Range Selection */}
      <div className="card">
        <div className="card-header">
          <h3 className="text-lg font-semibold text-gray-900">Select Date Range</h3>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 items-end">
          <div>
            <label htmlFor="start_date" className="form-label">
              Start Date
            </label>
            <input
              id="start_date"
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              data-testid="start-date-input"
              className="form-input"
            />
          </div>
          <div>
            <label htmlFor="end_date" className="form-label">
              End Date
            </label>
            <input
              id="end_date"
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              data-testid="end-date-input"
              className="form-input"
            />
          </div>
          <div>
            <button
              onClick={fetchReportsByDateRange}
              disabled={loading}
              data-testid="filter-reports-button"
              className="btn-primary w-full"
            >
              {loading ? <div className="spinner mx-auto"></div> : 'Filter Reports'}
            </button>
          </div>
          <div>
            <button
              onClick={downloadCombinedReport}
              disabled={loading || reports.length === 0}
              data-testid="download-combined-button"
              className="btn-secondary w-full"
            >
              Download Combined CSV
            </button>
          </div>
        </div>
      </div>

      {/* Reports Summary */}
      {reports.length > 0 && (
        <div className="card">
          <div className="card-header">
            <h3 className="text-lg font-semibold text-gray-900">Reports Summary</h3>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="card text-center">
              <h3 className="text-sm font-medium text-gray-500 mb-2">Available Reports</h3>
              <p className="text-3xl font-bold text-blue-600" data-testid="reports-count">
                {reports.length}
              </p>
            </div>
            <div className="card text-center">
              <h3 className="text-sm font-medium text-gray-500 mb-2">Total Orders</h3>
              <p className="text-3xl font-bold text-green-600" data-testid="total-orders">
                {reports.reduce((sum, report) => sum + (report.order_count || 0), 0)}
              </p>
            </div>
            <div className="card text-center">
              <h3 className="text-sm font-medium text-gray-500 mb-2">Date Range</h3>
              <p className="text-lg font-semibold text-purple-600" data-testid="date-range">
                {reports.length > 0 
                  ? `${reports[reports.length - 1]?.report_date} to ${reports[0]?.report_date}`
                  : 'No data'
                }
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Available Reports */}
      <div className="card">
        <div className="card-header">
          <h3 className="text-lg font-semibold text-gray-900">Available Daily Reports</h3>
        </div>
        {reports.length === 0 ? (
          <div className="text-center py-8 text-gray-500" data-testid="no-reports-message">
            {loading ? (
              <div className="spinner mx-auto"></div>
            ) : (
              <div>
                <p className="text-lg mb-2">No daily reports found for the selected date range.</p>
                <p className="text-sm">Reports are automatically generated when orders are older than 24 hours.</p>
              </div>
            )}
          </div>
        ) : (
          <div className="overflow-x-auto overflow-y-auto max-h-96">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50 sticky top-0">
                <tr>
                  <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-32">
                    Report Date
                  </th>
                  <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-24">
                    Orders Count
                  </th>
                  <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-32 hidden sm:table-cell">
                    Generated At
                  </th>
                  <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-28">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {reports.map((report) => (
                  <tr key={report.id} data-testid={`report-row-${report.report_date}`}>
                    <td className="px-3 sm:px-6 py-4 text-sm font-medium text-gray-900">
                      <div className="whitespace-nowrap">
                        {formatDate(report.report_date)}
                      </div>
                    </td>
                    <td className="px-3 sm:px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {report.order_count || 0} orders
                    </td>
                    <td className="px-3 sm:px-6 py-4 whitespace-nowrap text-sm text-gray-500 hidden sm:table-cell">
                      {new Date(report.created_at).toLocaleString()}
                    </td>
                    <td className="px-3 sm:px-6 py-4 whitespace-nowrap text-sm space-x-2">
                      <button
                        onClick={() => downloadIndividualReport(report.report_date)}
                        data-testid={`download-report-${report.report_date}`}
                        className="text-blue-600 hover:text-blue-800 font-medium text-xs sm:text-sm"
                      >
                        Download CSV
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Information */}
      <div className="card bg-blue-50">
        <div className="card-header">
          <h3 className="text-lg font-semibold text-blue-900">About Daily Reports</h3>
        </div>
        <div className="text-sm text-blue-800">
          <p className="mb-2">
            <strong>Automatic Generation:</strong> Daily reports are automatically created when orders become older than 30 hours.
          </p>
          <p className="mb-2">
            <strong>Order Management:</strong> Orders older than 30 hours are automatically archived and removed from the active orders view after daily reports are generated.
          </p>
          <p>
            <strong>Data Retention:</strong> Daily reports are kept for historical purposes and can be downloaded as CSV files for record-keeping and analysis.
          </p>
        </div>
      </div>
        </>
      )}
    </div>
  );
};

export default DailyReports;